/**
 * DataRetentionSetupModal Component
 *
 * Modal for configuring data retention policy.
 * Signed notes are retained for 7 years (required for compliance).
 * Unsigned notes can be auto-deleted after a user-specified period.
 */

import { useState } from 'react';
import { Info } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';

interface DataRetentionSetupModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DataRetentionFormData) => Promise<void>;
}

export interface DataRetentionFormData {
  unsignedNotesRetentionDays: number | null; // null means no auto-delete
}

const RETENTION_OPTIONS = [
  { value: 'none', label: 'Do not auto-delete', days: null },
  { value: '30', label: '30 days', days: 30 },
  { value: '60', label: '60 days', days: 60 },
  { value: '90', label: '90 days', days: 90 },
  { value: '180', label: '180 days (6 months)', days: 180 },
  { value: '365', label: '365 days (1 year)', days: 365 },
  { value: 'custom', label: 'Custom...', days: null },
];

const MIN_CUSTOM_DAYS = 1;
const MAX_CUSTOM_DAYS = 3650; // ~10 years

export function DataRetentionSetupModal({ open, onClose, onSubmit }: DataRetentionSetupModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [retentionOption, setRetentionOption] = useState<string>('none');
  const [customDays, setCustomDays] = useState<string>('');

  const handleRetentionChange = (event: SelectChangeEvent) => {
    setRetentionOption(event.target.value);
    // Clear custom days when switching away from custom
    if (event.target.value !== 'custom') {
      setCustomDays('');
    }
  };

  const handleCustomDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDays(event.target.value);
  };

  const getRetentionDays = (): number | null => {
    if (retentionOption === 'none') {
      return null;
    }
    if (retentionOption === 'custom') {
      const days = parseInt(customDays, 10);
      return isNaN(days) ? null : days;
    }
    const selectedOption = RETENTION_OPTIONS.find(opt => opt.value === retentionOption);
    return selectedOption?.days ?? null;
  };

  const validateCustomDays = (): string | null => {
    if (retentionOption !== 'custom') {
      return null;
    }
    if (!customDays.trim()) {
      return 'Please enter the number of days';
    }
    const days = parseInt(customDays, 10);
    if (isNaN(days)) {
      return 'Please enter a valid number';
    }
    if (days < MIN_CUSTOM_DAYS || days > MAX_CUSTOM_DAYS) {
      return `Please enter a value between ${MIN_CUSTOM_DAYS} and ${MAX_CUSTOM_DAYS} days`;
    }
    return null;
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate custom days if that option is selected
    const customDaysError = validateCustomDays();
    if (customDaysError) {
      setError(customDaysError);
      return;
    }

    setIsSubmitting(true);

    const retentionDays = getRetentionDays();

    try {
      await onSubmit({
        unsignedNotesRetentionDays: retentionDays,
      });
      setShowSuccess(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to save retention policy. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset state when closing
      setRetentionOption('none');
      setCustomDays('');
      setError(null);
      setShowSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure Data Retention Policy</DialogTitle>
      <DialogContent>
        {showSuccess ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your data retention policy has been saved successfully.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Your retention settings are now active. You can update these settings at any time.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set straightforward retention rules based on how you use JotPsych, with automatic
              deletion for unsigned notes after your chosen period. Signed notes stay retained for 7
              years, supporting audits, compliance, and peace of mind.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Signed Notes Info */}
            <Alert
              severity="info"
              icon={<Info />}
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Signed notes are retained for 7 years
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This is required for HIPAA compliance and audit purposes. Signed notes cannot be
                auto-deleted.
              </Typography>
            </Alert>

            {/* Unsigned Notes Retention */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Unsigned Notes Retention
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose when to automatically delete notes that have not been signed. This helps
                manage storage and removes incomplete work automatically.
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="retention-select-label">Auto-delete unsigned notes after</InputLabel>
                <Select
                  labelId="retention-select-label"
                  id="retention-select"
                  value={retentionOption}
                  label="Auto-delete unsigned notes after"
                  onChange={handleRetentionChange}
                  disabled={isSubmitting}
                >
                  {RETENTION_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Custom days input */}
              {retentionOption === 'custom' && (
                <TextField
                  label="Number of days"
                  type="number"
                  fullWidth
                  value={customDays}
                  onChange={handleCustomDaysChange}
                  disabled={isSubmitting}
                  inputProps={{ min: MIN_CUSTOM_DAYS, max: MAX_CUSTOM_DAYS }}
                  helperText={`Enter a value between ${MIN_CUSTOM_DAYS} and ${MAX_CUSTOM_DAYS} days`}
                  sx={{ mt: 2 }}
                />
              )}

              {retentionOption !== 'none' && retentionOption !== 'custom' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Unsigned notes older than{' '}
                  {RETENTION_OPTIONS.find(opt => opt.value === retentionOption)?.label} will be
                  automatically deleted.
                </Typography>
              )}

              {retentionOption === 'custom' && customDays && !validateCustomDays() && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Unsigned notes older than {customDays} days will be automatically deleted.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {showSuccess ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isSubmitting ? 'Saving...' : 'Save Policy'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
